import React from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { practitioners, soulJourneyWeeks, sessionIncludes, journeyExperience, images } from '../data/mockData';
import { Sparkles, Star, Clock, Heart, Award, CheckCircle } from 'lucide-react';
import { getResponsiveImageUrl } from '../lib/imageOptimization';

const SoulJourneyPage = () => {
  const handleEnrollNow = () => {
    window.open('https://buy.stripe.com/bJe7sK7298UI6c16ML0Fi02', '_blank', 'noopener noreferrer');
  };

  return (
    <div className="min-h-screen bg-black pt-20">
      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url(${getResponsiveImageUrl(images.soulJourneyHero, { width: 1600 })})`,
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-purple-900/95 via-purple-800/90 to-indigo-900/95"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="mb-8">
            <span className="inline-block px-6 py-3 bg-yellow-500/20 border border-yellow-400/30 rounded-full text-yellow-300 font-semibold text-lg mb-6">
              A six-week private journey
            </span>
            <p className="text-yellow-200 text-lg">(only offered 2x per year)</p>
          </div>

          <div className="max-w-4xl mx-auto mb-12">
            <p className="text-xl text-yellow-200 mb-6 italic">
              You've felt the shift. Something in your soul stirred awake. A sacred knowing whispered:
            </p>
            
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold mb-8 text-transparent bg-gradient-to-r from-yellow-400 to-yellow-300 bg-clip-text">
              "You're not meant to stay stuck here."
            </h1>

            <div className="text-white text-xl leading-relaxed space-y-4 mb-8">
              <p>You're meant to heal. To return. To rise.</p>
              <p>This isn't just a program. It's a sacred passage.</p>
              <p>A deeply personalized energetic recalibration guided by the most gifted soul alchemists in the field.</p>
              <p className="text-yellow-200">
                You'll be supported one-on-one through a sequence of quantum healing, 
                deep soul reclamation, and energetic evolution.
              </p>
            </div>

            <h2 className="text-3xl font-bold text-transparent bg-gradient-to-r from-yellow-400 to-yellow-300 bg-clip-text mb-8">
              Welcome to The Sacred Soul Journey™.
            </h2>
          </div>

          <Button 
            onClick={handleEnrollNow}
            className="bg-gradient-to-r from-yellow-500 to-yellow-400 hover:from-yellow-400 hover:to-yellow-300 text-black font-bold py-4 px-8 text-lg rounded-lg transition-all duration-300 transform hover:scale-105 shadow-xl"
          >
            I'M READY FOR MORE
          </Button>
        </div>
      </section>

      {/* What Is Sacred Soul Journey */}
      <section className="py-20 bg-gradient-to-b from-purple-950/30 to-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold text-transparent bg-gradient-to-r from-yellow-400 to-yellow-300 bg-clip-text mb-8">
              What Is the Sacred Soul Journey™?
            </h2>
            <p className="text-xl text-gray-300 max-w-4xl mx-auto leading-relaxed">
              The Sacred Soul Journey™ is a 6-week private, 1-on-1 experience designed to completely 
              shift your energetic architecture, dissolve lifetimes of internal blockages, and 
              reawaken the truth of who you really are.
            </p>
          </div>

          <div className="text-center mb-16">
            <h3 className="text-3xl font-bold text-yellow-300 mb-6">
              No matter what you're dealing with, this program has the power to heal and transform it!
            </h3>
            <p className="text-lg text-gray-300 mb-8">
              Guided by a certified practitioner of your choosing from The Wisdom Network™, 
              you'll move through our exclusive 3-phase framework:
            </p>
            <h4 className="text-4xl font-bold text-transparent bg-gradient-to-r from-yellow-400 to-yellow-300 bg-clip-text mb-8">
              Reclaim. Realign. Rise.™
            </h4>
            <p className="text-xl text-yellow-200">
              This is not a course. Not a group program. This is your soul's personal initiation.
            </p>
          </div>
        </div>
      </section>

      {/* What You'll Experience */}
      <section className="py-20 bg-gradient-to-b from-black to-purple-950/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-transparent bg-gradient-to-r from-yellow-400 to-yellow-300 bg-clip-text text-center mb-12">
            What You'll Experience
          </h2>
          
          <p className="text-center text-lg text-gray-300 mb-12">During these six 1:1 sessions, you will:</p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
            {journeyExperience.map((experience, index) => (
              <Card 
                key={index}
                className="bg-gradient-to-br from-purple-900/40 via-purple-800/30 to-purple-900/40 border border-purple-700/50 hover:border-yellow-500/50 transition-all duration-300 group"
              >
                <CardContent className="p-6 text-center">
                  <CheckCircle className="w-8 h-8 text-yellow-400 mx-auto mb-4" />
                  <p className="text-gray-300 group-hover:text-gray-200 transition-colors duration-300">
                    {experience}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mb-8">
            <p className="text-2xl font-bold text-yellow-300 mb-6">
              ALL TAILORED AND PERSONALIZED TO YOUR SPECIFIC JOURNEY
            </p>
            <div className="space-y-4 text-xl text-white">
              <p>You'll be seen.</p>
              <p>You'll be activated.</p>
              <p>You'll be forever changed.</p>
            </div>
          </div>

          <div className="text-center">
            <Button 
              onClick={handleEnrollNow}
              className="bg-gradient-to-r from-yellow-500 to-yellow-400 hover:from-yellow-400 hover:to-yellow-300 text-black font-bold py-4 px-8 text-lg rounded-lg transition-all duration-300 transform hover:scale-105 shadow-xl"
            >
              I CHOOSE ME
            </Button>
          </div>
        </div>
      </section>

      {/* Weekly Journey Map */}
      <section className="py-20 bg-gradient-to-b from-purple-950/30 to-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-transparent bg-gradient-to-r from-yellow-400 to-yellow-300 bg-clip-text text-center mb-16">
            Your Week-by-Week Sacred Soul Map
          </h2>

          <div className="space-y-8">
            {soulJourneyWeeks.map((week, index) => (
              <Card 
                key={index}
                className="bg-gradient-to-r from-purple-900/40 via-purple-800/30 to-purple-900/40 border border-purple-700/50 hover:border-yellow-500/50 transition-all duration-300"
              >
                <CardContent className="p-8">
                  <div className="flex items-start space-x-6">
                    <div className="flex-shrink-0">
                      <div className="w-16 h-16 bg-gradient-to-r from-yellow-400 to-yellow-500 rounded-full flex items-center justify-center">
                        <span className="text-black font-bold text-lg">WEEK {week.week}</span>
                      </div>
                    </div>
                    <div className="flex-grow">
                      <h3 className="text-2xl font-bold text-yellow-300 mb-4">{week.title}</h3>
                      <p className="text-gray-300 leading-relaxed text-lg">{week.description}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Session Includes */}
          <div className="mt-20">
            <h3 className="text-3xl font-bold text-transparent bg-gradient-to-r from-yellow-400 to-yellow-300 bg-clip-text text-center mb-12">
              Each Session Includes:
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {sessionIncludes.map((item, index) => (
                <Card 
                  key={index}
                  className="bg-gradient-to-br from-purple-900/40 via-purple-800/30 to-purple-900/40 border border-purple-700/50"
                >
                  <CardContent className="p-6 flex items-center space-x-4">
                    <CheckCircle className="w-6 h-6 text-yellow-400 flex-shrink-0" />
                    <p className="text-gray-300 text-lg">{item}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Practitioners Section */}
      <section className="py-20 bg-gradient-to-b from-black to-purple-950/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-transparent bg-gradient-to-r from-yellow-400 to-yellow-300 bg-clip-text mb-8">
              Your Guide. Your Healing. Your Sacred Space.
            </h2>
            <p className="text-xl text-gray-300 max-w-4xl mx-auto leading-relaxed">
              You'll choose your personal practitioner from The Wisdom Network™, a team of elite, 
              vetted soul practitioners trained in advanced energy mastery under Monica Bey's energetic lineage.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-8 mb-16">
            {practitioners.map((practitioner) => (
              <Card 
                key={practitioner.id}
                className="bg-gradient-to-br from-purple-900/40 via-purple-800/30 to-purple-900/40 border border-purple-700/50 hover:border-yellow-500/50 transition-all duration-300 hover:transform hover:scale-105 group"
              >
                <CardContent className="p-4 text-center">
                  <div className="mb-4">
                    <img
                      src={getResponsiveImageUrl(practitioner.image, { width: 256 })}
                      alt={practitioner.name}
                      className="w-20 h-20 rounded-full mx-auto object-cover border-2 border-yellow-400/30 group-hover:border-yellow-400/60 transition-all duration-300"
                      loading="lazy"
                      decoding="async"
                    />
                  </div>
                  <h3 className="text-yellow-300 font-bold text-sm mb-1 group-hover:text-yellow-200 transition-colors duration-300">
                    {practitioner.title}
                  </h3>
                  <p className="text-gray-400 text-xs">{practitioner.name}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center">
            <h3 className="text-3xl font-bold text-yellow-300 mb-6">
              They Are More Than Coaches.
            </h3>
            <h4 className="text-2xl font-bold text-transparent bg-gradient-to-r from-yellow-400 to-yellow-300 bg-clip-text mb-8">
              They are Soul Alchemists
            </h4>
            <div className="text-lg text-gray-300 space-y-4 mb-12">
              <p>intuitive healers, energy architects, and frequency activators.</p>
              <p>They help you uncover truths your conscious mind can't reach.</p>
              <p>They guide you where your spirit has longed to go.</p>
              <p>They hold space for the version of you that's been waiting to emerge.</p>
            </div>

            <Button 
              onClick={handleEnrollNow}
              className="bg-gradient-to-r from-yellow-500 to-yellow-400 hover:from-yellow-400 hover:to-yellow-300 text-black font-bold py-4 px-8 text-lg rounded-lg transition-all duration-300 transform hover:scale-105 shadow-xl"
            >
              ALIGN MY SOUL PATH
            </Button>
          </div>
        </div>
      </section>

      {/* Investment Section */}
      <section className="py-20 bg-gradient-to-b from-purple-950/30 to-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="mb-12">
            <h2 className="text-4xl font-bold text-transparent bg-gradient-to-r from-yellow-400 to-yellow-300 bg-clip-text mb-8">
              Enrollment Is Limited. Only Offered Twice Per Year
            </h2>
            <p className="text-xl text-gray-300 mb-8">
              The Sacred Soul Journey™ is a signature experience offered only twice a year 
              to preserve the sacred integrity of the work.
            </p>
            <p className="text-lg text-yellow-200 mb-8">
              Enrollment is now open for the 2025 Journey. Spots are limited and assigned on a first-come basis.
            </p>
            <h3 className="text-2xl font-bold text-yellow-300">
              If your soul is resonating... trust it.
            </h3>
          </div>

          <Card className="max-w-2xl mx-auto bg-gradient-to-br from-purple-900/60 via-purple-800/40 to-purple-900/60 border border-yellow-500/50 mb-12">
            <CardContent className="p-12 text-center">
              <h3 className="text-3xl font-bold text-transparent bg-gradient-to-r from-yellow-400 to-yellow-300 bg-clip-text mb-4">
                Your Investment
              </h3>
              
              <div className="mb-6">
                <span className="text-6xl font-bold text-yellow-400">$1,222</span>
              </div>
              
              <p className="text-lg text-gray-300 mb-4">
                <span className="line-through">Normally $1,500</span>
              </p>
              
              <p className="text-yellow-200 mb-6">
                available now at exclusive pricing for a short time
              </p>
              
              <p className="text-sm text-gray-400">
                Flexible payment plans available with Klarna™ and Affirm™.
              </p>
            </CardContent>
          </Card>

          <div className="mb-12">
            <h3 className="text-3xl font-bold text-transparent bg-gradient-to-r from-yellow-400 to-yellow-300 bg-clip-text mb-8">
              Enrollment is Now Open
            </h3>
            <p className="text-lg text-gray-300 mb-8">
              Offered only twice per year. This portal will close soon
            </p>
            
            <div className="max-w-4xl mx-auto text-left mb-8">
              <p className="text-lg text-gray-300 mb-4">
                Begin your Sacred Soul Journey on your timeline. You'll schedule your 6 sessions 
                directly with your chosen practitioner based on your schedule.
              </p>
              <p className="text-lg text-yellow-200 mb-6">
                Your experience is personalized from start to finish, including when it begins. 
                Each journey follows a 6-week guided structure once it starts.
              </p>
              
              <div className="text-center">
                <h4 className="text-xl font-bold text-yellow-300 mb-4">WHAT'S INCLUDED:</h4>
                <ul className="text-left text-lg text-gray-300 space-y-2 max-w-2xl mx-auto">
                  <li>• 6 private transformational healing sessions</li>
                  <li>• Personalized quantum energy work</li>
                  <li>• Intuitive support and soul recalibration</li>
                  <li>• Integration rituals and take-home practices</li>
                  <li>• A full 6-week sacred framework for real soul evolution</li>
                </ul>
              </div>
            </div>

            <div className="mb-8">
              <p className="text-2xl font-bold text-yellow-300 mb-4">This is more than healing.</p>
              <p className="text-2xl font-bold text-transparent bg-gradient-to-r from-yellow-400 to-yellow-300 bg-clip-text">
                It's energetic rebirth.
              </p>
            </div>

            <Button 
              onClick={handleEnrollNow}
              className="bg-gradient-to-r from-yellow-500 to-yellow-400 hover:from-yellow-400 hover:to-yellow-300 text-black font-bold py-4 px-8 text-xl rounded-lg transition-all duration-300 transform hover:scale-105 shadow-xl mb-12"
            >
              ENTER THE PORTAL
            </Button>
          </div>

          <div className="text-center">
            <h3 className="text-3xl font-bold text-transparent bg-gradient-to-r from-yellow-400 to-yellow-300 bg-clip-text mb-8">
              You Are Ready to Rise?
            </h3>
            <div className="text-xl text-white space-y-4 mb-8">
              <p>This moment is about answering the soul call you've been hearing for lifetimes.</p>
              <p>If your energy is stirring…</p>
              <p>If your heart is whispering "this is it"…</p>
              <p className="text-yellow-200 font-semibold">Then this is your time.</p>
            </div>

            <Button 
              onClick={handleEnrollNow}
              className="bg-gradient-to-r from-yellow-500 to-yellow-400 hover:from-yellow-400 hover:to-yellow-300 text-black font-bold py-4 px-8 text-xl rounded-lg transition-all duration-300 transform hover:scale-105 shadow-xl"
            >
              LET THE HEALING BEGIN
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SoulJourneyPage;